import {createContext, ReactNode, useContext, useMemo} from "react"
import {
  findModelConfig,
  findProviderConfig,
  isActiveModel,
  ModelConfig,
} from "~/.shared/languageModel/ModelConfig"
import {ModelProviderConfig} from "~/.shared/languageModel/ModelProviderConstants"
import {ModelName} from "~/generated/modelNames"

type ModelConfigsContextValue = {
  modelConfigs: ModelConfig[]
  providerConfigs: ModelProviderConfig[]
}

const ModelConfigsContext = createContext<ModelConfigsContextValue | null>(null)

export const ModelConfigsContextProvider = ({
  modelConfigs,
  providerConfigs,
  children,
}: ModelConfigsContextValue & {children: ReactNode}) => {
  const value = useMemo(
    () => ({modelConfigs, providerConfigs}),
    [modelConfigs, providerConfigs]
  )
  return (
    <ModelConfigsContext.Provider value={value}>
      {children}
    </ModelConfigsContext.Provider>
  )
}

const useModelConfigContext = (): ModelConfigsContextValue => {
  const ctx = useContext(ModelConfigsContext)
  if (!ctx) {
    throw new Error(
      "ModelConfigsContext not found. Ensure <ModelConfigsContextProvider> wraps your tree (root.tsx)."
    )
  }
  return ctx
}

/** Full list (includes inactive — for resolving historical model_name strings). */
export const useModelConfigs = (): ModelConfig[] => useModelConfigContext().modelConfigs

/** All provider configs. */
export const useProviderConfigs = (): ModelProviderConfig[] => useModelConfigContext().providerConfigs

/** Active models in DB order, suitable for pickers. */
export const useActiveModelConfigs = (): ModelConfig[] =>
  useModelConfigContext().modelConfigs.filter(isActiveModel)

/** Look up a single model config by name. Throws if not found. */
export const useModelConfig = (modelName: ModelName | string): ModelConfig =>
  findModelConfig(useModelConfigContext().modelConfigs, modelName)

/** Look up the provider config for a given model name. */
export const useProviderConfigForModel = (modelName: ModelName | string): ModelProviderConfig => {
  const {modelConfigs, providerConfigs} = useModelConfigContext()
  return findProviderConfig(modelConfigs, providerConfigs, modelName)
}




